<template>
    <FormSection
        :full-height="false"
        title="Employee Scheduler"
        :application-id="visitDetails.Application"
        :show-product-pill="true"
        :show-background="false"
    >
        <template #content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                <span class="mt-1">
                    <b>Loading...</b>
                </span>
            </section>
            <section class="task-details" v-else>
                <div class="container row m-0 p-1">
                    <div class="form-group col-12 mt-2">
                        <strong for="task-name" class="me-2">Client:</strong>
                        <span class="bold">{{ visitDetails.FirstName }} {{ visitDetails.LastName }}</span>
                        <p class="mt-2 mb-1">
                            <strong class="me-2">Current/Last Care Period:</strong>
                            <span>{{ carePeriod }}</span>
                        </p>
                        <p class="mt-2">
                            <strong class="me-2">Agency:</strong>
                            <span>{{ visitDetails.AgencyName }}</span>
                        </p>
                    </div>
                    <!-- Statement Range -->
                    <div class="col-auto form-group">
                        <div class="input-group flex-nowrap">
                            <div>
                                <label for="date"> Start Date </label><span class="text-danger">*</span>
                                <DatePicker :getDate="taskDateChanged">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            type="text"
                                            name="startDateTime"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDateInput(newVisitDetails.startDateTime.date)"
                                            :class="`form-control rounded-left-5  ${
                                                !v$.newVisitDetails.startDateTime.date.$error
                                                    ? ''
                                                    : 'field-invalid mt-0'
                                            }`"
                                            @blur="v$.newVisitDetails.startDateTime.date.$touch"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <span v-if="v$.newVisitDetails.startDateTime.date.$error" class="field-invalid"
                                        >Start Date is required.</span
                                    >
                                </DatePicker>
                            </div>
                            <div class="pt-4 w-5">
                                <hr />
                            </div>
                            <div>
                                <label for="endDate"> End Date </label
                                ><span v-if="overrideShiftLength" class="text-danger">*</span>
                                <DatePicker id="endDate" :getDate="taskDateChanged">
                                    <div class="input-group date datepickerInput">
                                        <input
                                            type="text"
                                            name="endDateTime"
                                            class="form-control rounded-left-5"
                                            placeholder="MM/DD/YYYY"
                                            :value="formatDateInput(newVisitDetails.endDateTime.date)"
                                            :disabled="!overrideShiftLength && !scheduleWithoutTime"
                                            date-format-only
                                            :class="`form-control rounded-left-5 ${
                                                !v$.newVisitDetails.endDateTime.date.$error ? '' : 'field-invalid mt-0'
                                            }`"
                                            @blur="v$.newVisitDetails.endDateTime.date.$touch"
                                        />
                                        <div class="input-group-addon input-group-append">
                                            <div class="input-group-text">
                                                <i class="fa fa-calendar"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <span v-if="v$.newVisitDetails.endDateTime.date.$error" class="field-invalid"
                                        >End Date is required.</span
                                    >
                                </DatePicker>
                            </div>
                        </div>
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group col-10 col-md-3">
                        <label for="shift-length"> Shift Length </label>
                        <select
                            class="form-select"
                            v-model="shiftLength"
                            :disabled="scheduleWithoutTime || overrideShiftLength"
                        >
                            <option v-for="i of 12" :value="i">{{ i }} {{ i === 1 ? "Hour" : "Hours" }}</option>
                        </select>
                    </div>
                    <div class="col-auto form-group pb-2" v-if="!scheduleWithoutTime">
                        <div>
                            <div class="input-group flex-nowrap">
                                <div>
                                    <label for="date"> Start Time </label><span class="text-danger">*</span>
                                    <TimePicker @update-props="changeStartTime">
                                        <input
                                            placeholder="hh:mm AM"
                                            type="text"
                                            autocomplete="off"
                                            maxlength="8"
                                            :value="newVisitDetails.startDateTime.time"
                                            :class="`form-control ${
                                                !v$.newVisitDetails.startDateTime.time.$error
                                                    ? ''
                                                    : 'field-invalid mt-0'
                                            }`"
                                            @blur="v$.newVisitDetails.startDateTime.time.$touch"
                                        />
                                    </TimePicker>
                                    <div v-if="v$.newVisitDetails.startDateTime.time.$error" class="field-invalid">
                                        Start Time is required.
                                    </div>
                                </div>
                                <div class="w-5 pt-4">
                                    <hr />
                                </div>
                                <div>
                                    <label for="date"> End Time </label>
                                    <span v-if="overrideShiftLength" class="text-danger">*</span>
                                    <TimePicker @update-props="changeEndTime" id="schedule-hc-task-end-time">
                                        <input
                                            placeholder="hh:mm AM"
                                            type="text"
                                            class="form-control"
                                            autocomplete="off"
                                            maxlength="8"
                                            v-model="newVisitDetails.endDateTime.time"
                                            :disabled="!overrideShiftLength"
                                            :class="`form-control ${
                                                !v$.newVisitDetails.endDateTime.time.$error ? '' : 'field-invalid mt-0'
                                            }`"
                                            @blur="v$.newVisitDetails.endDateTime.time.$touch"
                                        />
                                    </TimePicker>
                                    <div v-if="v$.newVisitDetails.endDateTime.time.$error" class="field-invalid">
                                        End Time is required.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-group form-check custom-checkbox col-11 mt-2 ms-3 d-block d-md-flex">
                        <div class="d-flex d-md-inline-block">
                            <input
                                type="checkbox"
                                id="schedule-without-time"
                                class="form-check-input"
                                v-model="scheduleWithoutTime"
                            />
                            <label class="form-check-label" for="schedule-without-time"> Schedule Without Time </label>
                        </div>
                        <div class="d-flex d-md-inline-block ms-md-5">
                            <input
                                type="checkbox"
                                id="override-shift-length"
                                class="form-check-input"
                                v-model="overrideShiftLength"
                                :disabled="scheduleWithoutTime"
                            />
                            <label class="form-check-label" for="override-shift-length"> Override Shift Length </label>
                        </div>
                    </div>
                    <div class="w-100">
                        <hr />
                    </div>
                    <div class="form-group col-6 col-md-3">
                        <div class="w-100"></div>
                        <div class="form-group">
                            <label>Recurrence</label>
                            <select
                                class="form-select"
                                :value="newVisitDetails.recurrence.type"
                                @change="handleRecurrenceChange"
                                name="recurrence"
                            >
                                <option
                                    v-for="[key, value] of enumToMap(recurrenceType)"
                                    :key="key"
                                    :value="value.value"
                                >
                                    {{ value.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <!-- Daily -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Daily">
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        name="endDate"
                                        type="text"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">{{ i }} {{ i == 1 ? "day" : "days" }}</option>
                                </select>
                            </div>
                        </div>
                    </template>
                    <!-- Weekly -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Weekly">
                        <div class="form-group col-6 col-md-3">
                            <label for="days" class="mb-2"> Repeat On </label><span class="text-danger">*</span>
                            <DayPicker
                                :selected-days-string="newVisitDetails.recurrence.daysOfWeek"
                                v-on:updateSelectedDays="updateDaysOfWeek"
                            />
                            <span v-if="v$.newVisitDetails.recurrence.daysOfWeek.$error" class="field-invalid"
                                >Repeat on Date is required.</span
                            >
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">{{ i }} {{ i == 1 ? "week" : "weeks" }}</option>
                                </select>
                            </div>
                        </div>
                    </template>
                    <!-- Monthly -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Monthly">
                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat Every </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatNumber"
                                    name="repeat-every"
                                >
                                    <option v-for="i of 31" :value="i">
                                        {{ i }} {{ i == 1 ? "month" : "months" }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <div class="w-100"></div>
                            <div class="form-group">
                                <label>Repeat By </label>
                                <select
                                    class="form-select"
                                    :value="newVisitDetails.recurrence.repeatBy"
                                    name="repeat-every"
                                >
                                    <option :value="1">Day of Month</option>
                                    <option :value="2">Day of Week</option>
                                </select>
                            </div>
                        </div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                    </template>
                    <!-- Flexible -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.Flexible">
                        <div class="w-100"></div>
                        <div class="form-group col-12">
                            <label for="date"> Select Days </label><span class="text-danger">*</span>
                            <MultiDatePickerHC
                                :result="visitDates"
                                v-on:updateResult="updateResult"
                                :current-care-details="currentCareDetailList"
                                ><input
                                    :class="`form-control ${
                                        !v$.newVisitDetails.recurrence.flexibleDates.$error ? '' : 'field-invalid mt-0'
                                    }`"
                                    @blur="v$.newVisitDetails.recurrence.flexibleDates.$touch"
                                />
                            </MultiDatePickerHC>
                            <span v-if="v$.newVisitDetails.recurrence.flexibleDates.$error" class="field-invalid"
                                >Dates are required.</span
                            >
                        </div>
                        <div></div>
                    </template>
                    <!-- 60 days -->
                    <template v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.SixtyDays">
                        <div class="w-100"></div>
                        <div class="form-group col-6 col-md-3">
                            <label for="date"> Repeat Until </label><span class="text-danger">*</span>
                            <DatePicker :getDate="taskDateChanged">
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="date"
                                        type="text"
                                        name="endDate"
                                        placeholder="Select Date"
                                        date-format-only
                                        :value="formatDateInput(newVisitDetails.recurrence.endDate)"
                                        :class="`form-control ${
                                            !v$.newVisitDetails.recurrence.endDate.$error ? '' : 'field-invalid mt-0'
                                        }`"
                                        @blur="v$.newVisitDetails.recurrence.endDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="v$.newVisitDetails.recurrence.endDate.$error" class="field-invalid"
                                    >Repeat Until Date is required.</span
                                >
                            </DatePicker>
                        </div>
                    </template>
                    <!-- End of recurrence -->
                    <div class="w-100">
                        <hr />
                    </div>
                    <div class="form-group col-10 col-md-5 col-lg-3">
                        <label for="payment-source"> Payment Source </label><span class="text-danger">*</span>
                        <select
                            v-show="!isPaymentSourceLoading"
                            id="payment-source"
                            v-model="newVisitDetails.payorId"
                            :class="`form-select ${!v$.newVisitDetails.payorId.$error ? '' : 'field-invalid mt-0'}`"
                            @blur="v$.newVisitDetails.payorId.$touch"
                        >
                            <option :value="null">-- Select Payment Source --</option>
                            <option v-for="payor of payorList" :value="payor.insuranceId">
                                {{ payor.name }} ({{ payor.typeName }})
                            </option>
                        </select>
                        <span v-if="v$.newVisitDetails.payorId.$error" class="field-invalid"
                            >Payment Source is required.</span
                        >
                        <div v-show="isPaymentSourceLoading"><span class="shimmer-loading w-100 mt-2"></span></div>
                    </div>
                    <div class="form-group col-10 col-md-5 col-lg-3">
                        <label for="task"> Task </label><span class="text-danger">*</span>
                        <div v-if="!!taskDropdownSelectOptions" class="multi-select-filter">
                            <GroupDropdownSelect
                                :drop-down-options="taskDropdownSelectOptions"
                                label-text="Task"
                                :selected-value="newVisitDetails.customNoteId"
                                :selected-text="newVisitDetails.taskName"
                                :selected-id="newVisitDetails.disciplineTask"
                                v-on:updateSelectFields="updateSelectFields"
                                :class="` ${!v$.newVisitDetails.disciplineTask.$error ? '' : 'field-invalid mt-0'}`"
                                @blur="v$.newVisitDetails.disciplineTask.$touch"
                            />
                            <span v-if="v$.newVisitDetails.disciplineTask.$error" class="field-invalid"
                                >Task is required.</span
                            >
                        </div>
                    </div>
                    <div class="w-100">
                        <hr />
                    </div>
                    <div
                        class="form-group col-6 col-md-3"
                        v-if="newVisitDetails.recurrence.type == recurrenceTypeEnum.None && !scheduleWithoutTime"
                    >
                        <label for="user-new"> Caregiver/Employee </label>
                        <i class="fas fa-question-circle tooltip-blue" v-tooltip="{ title: careGivTooltip }"> </i>
                        <MultiSelectDropdown
                            :has-actions="true"
                            class="text-center"
                            :selected-items="selectedConflictTypes"
                            v-on:selectedItemsUpdated="selectedItemsUpdated"
                            :options="conflictTypes"
                            :show-icons="true"
                            :disabled="isEmployeeListLoading || !canShowPremium"
                            :find-a-match="true"
                        />
                    </div>
                    <div
                        :class="`form-group col-10 col-md-6 col-lg-3 ${
                            newVisitDetails.recurrence.type == recurrenceTypeEnum.None ? 'pt-1' : ''
                        }`"
                    >
                        <label for="employee">{{
                            newVisitDetails.recurrence.type != recurrenceTypeEnum.None ? "Caregiver/Employee" : ""
                        }}</label>
                        <GroupDropdownSelect
                            v-if="!isEmployeeListLoading"
                            :drop-down-options="employeeDropdownSelectOptions"
                            :dropdownList="employeeDropdownSelectList"
                            :isList="scheduleWithoutTime || newVisitDetails.recurrence.type != 0"
                            label-text="Employee"
                            :selected-value="newVisitDetails.userId"
                            :selected-text="displayName"
                            v-on:updateSelectFields="updateSelectEmpFields"
                            :show-icons="true"
                            :disabled="employeeIsPreSelected"
                        />
                        <div v-else><span class="shimmer-loading w-100 mt-2"></span></div>
                    </div>
                    <div
                        class="col-6 col-md-12 col-lg-3 p-0 m-0 mt-4 mt-md-0 mt-lg-4 ms-lg-4"
                        v-if="!employeeIsPreSelected"
                    >
                        <div class="form-group col-12" v-if="canShowPremium">
                            <div class="find-ai-button-section" @click="openFindAMatch($event)">
                                <button class="button find-ai-button">Find a Match</button>
                            </div>
                        </div>
                        <div class="form-group col-12" v-else>
                            <button
                                class="find-ai-disabled-btn ai-disabled"
                                v-tooltip="{
                                    title: `Contact Axxess to unlock this <br /> premium Axxess intelligence feature.`,
                                    placement: 'top',
                                    className: 'lock-premium'
                                }"
                            >
                                Find a Match
                                <img src="../../../../assets/images/lock.svg" />
                            </button>
                        </div>
                    </div>
                    <div class="w-100"></div>
                    <div class="d-flex">
                        <p class="me-2"><i class="fa fa-solid fa-star icon-gold"></i> - Favorite Staff</p>
                        <p class="me-2"><i class="fa-regular fa-star icon-gold"></i> - Oriented Staff</p>
                        <p><i class="fa fa-ban"></i> - Do Not Send Staff</p>
                    </div>
                    <div class="w-100"></div>
                    <div class="form-group custom-control form-check col-10 mt-2 ms-3" v-if="showPostToAxxessCare">
                        <div>
                            <input
                                type="checkbox"
                                id="billable"
                                class="form-check-input"
                                :checked="newVisitDetails.postToAxxessCare"
                                v-model="newVisitDetails.postToAxxessCare"
                            />
                            <label class="custom-control-label" for="billable"> Post to AxxessCARE </label>
                        </div>
                    </div>
                    <div v-if="newVisitDetails.postToAxxessCare" class="form-group col-10 col-md-6 col-lg-3 mt-2">
                        <label for="task-name"> Task Type: </label>
                        <select
                            id="task-name"
                            :class="`form-select ${
                                !v$.newVisitDetails.discipline.$error && newVisitDetails.postToAxxessCare
                                    ? ''
                                    : 'field-invalid mt-0'
                            }`"
                            @change="onTaskTypeChange(newVisitDetails.discipline)"
                            v-model="newVisitDetails.discipline"
                        >
                            <option :value="null">Select Task Type</option>
                            <option v-for="(task, index) of specialties" :value="task.id" :key="index">
                                {{ task.name }}
                            </option>
                        </select>
                        <span
                            v-if="v$.newVisitDetails.discipline?.$error && newVisitDetails.postToAxxessCare"
                            class="field-invalid"
                            >Task Type is required.</span
                        >
                    </div>
                    <div class="w-100"></div>
                    <div class="col-10 col-md-6 form-group">
                        <label for="comments"> Comment </label>
                        <CommentInput
                            id="comments"
                            input-name="nonadmit-comments"
                            :show-template-typeahead="false"
                            :comment-required="false"
                            :result="newVisitDetails.comments"
                            :max-length="1000"
                            v-on:resultUpdated="resultUpdated"
                        />
                    </div>
                </div>
            </section>
            <div class="d-flex flex-wrap-reverse flex-md-nowrap justify-content-center my-2" v-if="!isLoading">
                <span v-if="isSaving">
                    <button class="btn btn-primary">
                        <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                    </button>
                </span>
                <span v-else>
                    <button class="btn btn-primary" @click="scheduleVisit(SaveEnum.SaveAndExit)">Save and Exit</button>
                    <SaveNotifyUser
                        :agency-id="newVisitDetails.agencyId"
                        button-name="Save and Notify Employee"
                        v-on:handleSaveVisit="scheduleVisit(SaveEnum.SaveAndNotifyEmployee)"
                    />
                    <button class="btn btn-primary ms-2" @click="scheduleVisit(SaveEnum.SaveAndAddAnother)">
                        Save and Add Another
                    </button>
                    <button type="button" class="ms-2 btn btn-md btn-outline-secondary" @click="cancel()">
                        Cancel
                    </button>
                </span>
            </div>
        </template>
    </FormSection>
</template>

<style src="./ScheduleVisitHomeCare.scss"></style>
<script lang="ts" src="./ScheduleVisitHomeCare.ts"></script>
